import { Button } from '@/shared/ui/button';
import { Textarea } from '@/shared/ui/textarea';
import {
  assistantComposerClasses,
  assistantComposerRowClasses,
} from '@/modules/assistant/parts/styles';

export type ComposerProps = {
  /** What the person has written so far. */
  value: string;
  /** Called with the new text on every keystroke. */
  onChange: (value: string) => void;
  /** Sends the message. */
  onSend: () => void;
  /** True while a turn is in flight. */
  isSending: boolean;
};

/**
 * The composer.
 *
 * The Send control is disabled while the composer is empty, so an empty turn is
 * never sent — the API would refuse it, and a refusal the person could have
 * avoided is a poor first impression of the assistant.
 *
 * ---------------------------------------------------------------------------
 * ENTER SENDS, SHIFT+ENTER DOES NOT
 * ---------------------------------------------------------------------------
 * A chat composer that makes the person reach for the mouse to send a
 * question is a composer that is used less. So ENTER sends — and SHIFT+ENTER
 * inserts a newline, which is what a person writing more than one line
 * expects.
 *
 * The handler is on the field rather than on a form, and it declines to send
 * while a turn is in flight, so a second ENTER cannot open a second turn on
 * the same conversation.
 */
export function Composer({ value, onChange, onSend, isSending }: ComposerProps) {
  const canSend = value.trim() !== '' && !isSending;

  return (
    <div className={assistantComposerClasses}>
      <Textarea
        label="Message the assistant"
        hideLabel
        rows={2}
        value={value}
        placeholder="Ask about this page"
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || event.shiftKey) return;
          // A newline is a newline when the person asked for one, and a send
          // when they did not.
          event.preventDefault();
          if (canSend) onSend();
        }}
      />

      <div className={assistantComposerRowClasses}>
        <Button size="sm" onClick={onSend} disabled={!canSend} loading={isSending}>
          Send
        </Button>
      </div>
    </div>
  );
}
